import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { z } from "zod";

import { formatAuthError, useResetPassword } from "@/features/auth/model/useAuth";
import { cn } from "@/shared/lib/cn";
import { Button, FormHint, Input, Label } from "@/shared/ui";

const schema = z
  .object({
    new_password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .max(128, "Password must be at most 128 characters"),
    confirm_password: z.string(),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    path: ["confirm_password"],
    message: "Passwords don't match",
  });

type FormValues = z.infer<typeof schema>;

interface Props {
  token: string;
}

export function ResetPasswordForm({ token }: Props) {
  const reset = useResetPassword();
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { new_password: "", confirm_password: "" },
  });

  const onSubmit = handleSubmit((values) =>
    reset.mutate({ token, new_password: values.new_password }),
  );

  if (reset.isSuccess) {
    return (
      <div className="space-y-4">
        <div className="rounded-md border border-success bg-success-light px-4 py-3 text-sm text-success-foreground">
          <p className="font-medium">Password updated</p>
          <p className="mt-1">
            You can now sign in with your new password.
          </p>
        </div>
        <Link
          to="/login"
          className="inline-flex w-full items-center justify-center rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
        >
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <p className="text-sm text-copy-muted">
        Choose a new password for your OneShift account.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="new_password">New password</Label>
        <div className="relative">
          <Input
            id="new_password"
            type={showPassword ? "text" : "password"}
            autoComplete="new-password"
            autoFocus
            invalid={!!errors.new_password}
            className="pr-10"
            {...register("new_password")}
          />
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            className={cn(
              "absolute inset-y-0 right-0 flex items-center px-3 text-copy-muted transition-colors",
              "hover:text-copy focus-visible:outline-none focus-visible:text-copy",
            )}
            tabIndex={-1}
          >
            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>
        {errors.new_password && <FormHint error>{errors.new_password.message}</FormHint>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="confirm_password">Confirm new password</Label>
        <Input
          id="confirm_password"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          invalid={!!errors.confirm_password}
          {...register("confirm_password")}
        />
        {errors.confirm_password && (
          <FormHint error>{errors.confirm_password.message}</FormHint>
        )}
      </div>

      {reset.error && (
        <div className="rounded-md border border-destructive bg-destructive-light px-3 py-2 text-sm text-destructive-foreground">
          {formatAuthError(reset.error)}
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        className="w-full"
        disabled={isSubmitting || reset.isPending}
      >
        {reset.isPending ? "Updating…" : "Update password"}
      </Button>

      <p className="text-center text-sm text-copy-muted">
        <Link to="/login" className="font-medium text-primary hover:underline">
          Back to sign in
        </Link>
      </p>
    </form>
  );
}
